import Box from "@mui/material/Box";
import React, {useEffect, useRef, useState} from "react";
import {Tooltip, Typography} from "@mui/material";
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ButtonAdd from "../button/ButtonAdd";
import {getCompositionImageUrl, getSortedCompositionPictures} from "../../../utils/getCompositionPictures";
import {
    formatCatalogCardSizeLines,
    formatCatalogDraftCardSizeLines,
    formatCatalogPriceRub,
    formatCompositionShortTopBottom,
    formatCompositionSizeMm,
} from "../../../utils/compositionMediaUtils";

const CARD_COMPACT_MQ = "@media (max-width: 1440px)";
const FLUID_HALF_COMPACT_MQ = "@media (max-width: 1450px)";
const FLUID_HALF_COMPACT_MQ_1250 = "@media (max-width: 1250px)";
/** Устройства без наведения курсором (сенсор). */
export const HOVER_NONE_MQ = "@media (hover: none)";
const HOVER_HOVER_MQ = "@media (hover: hover)";
const CARD_BUTTON_HEIGHT = 42;
/** Отступ между текстом и кнопкой в каталоге (в слоте при hover). */
const CATALOG_CARD_BUTTON_GAP_PX = 12;
const CATALOG_CARD_BUTTON_SLOT_MAX_PX = CARD_BUTTON_HEIGHT + CATALOG_CARD_BUTTON_GAP_PX;
/** Высота img-box в каталоге относительно ширины карточки. */
export const CATALOG_GRID_IMAGE_HEIGHT_CQW_RATIO = 1;
/** Высота области фото в карусели кухонь (на главной) относительно ширины карточки. */
export const KITCHEN_CAROUSEL_IMAGE_HEIGHT_CQW_RATIO = 17 / 20;

export const CATALOG_GRID_CARD_PADDING_TOP_PX = 20;
export const CATALOG_GRID_CARD_PADDING_BOTTOM_PX = 12;
export const CATALOG_GRID_CARD_INNER_GAP_PX = 16;
/** Текстовый блок в покое: заголовок (2 строки), размеры, описание (2 строки), характеристики, цена. */
export const CATALOG_CARD_TITLE_FONT_SIZE_PX = 22;
export const CATALOG_CARD_TITLE_LINE_HEIGHT = 1.2;
export const CATALOG_CARD_TITLE_LINE_COUNT = 2;
export const CATALOG_CARD_TITLE_BLOCK_MIN_HEIGHT_PX =
    CATALOG_CARD_TITLE_FONT_SIZE_PX * CATALOG_CARD_TITLE_LINE_HEIGHT * CATALOG_CARD_TITLE_LINE_COUNT;
export const CATALOG_GRID_CARD_BODY_SIZER_PX = 204 + CATALOG_CARD_TITLE_FONT_SIZE_PX * CATALOG_CARD_TITLE_LINE_HEIGHT;

export const getCatalogGridCardFlowSizerHeightCss = ({withButtonSlot = false} = {}) => {
    const bodyPx = CATALOG_GRID_CARD_BODY_SIZER_PX + (withButtonSlot ? CATALOG_CARD_BUTTON_SLOT_MAX_PX : 0);
    return `calc(${CATALOG_GRID_CARD_PADDING_TOP_PX + CATALOG_GRID_CARD_PADDING_BOTTOM_PX}px + ${CATALOG_GRID_CARD_INNER_GAP_PX}px + 100cqw * ${CATALOG_GRID_IMAGE_HEIGHT_CQW_RATIO} + ${bodyPx}px)`;
};

const buildCatalogCardExpandedInnerSx = ({
    isCatalogGridCard,
    isKitchenCarouselCard,
    catalogGridImgAreaHoverMaxHeightCss,
    kitchenImgAreaHoverHeightCss,
}) => ({
    ".catalog-button-slot": {
        maxHeight: `${CATALOG_CARD_BUTTON_SLOT_MAX_PX}px`,
    },
    ...(isCatalogGridCard && {
        ".catalog-img-area": {
            maxHeight: catalogGridImgAreaHoverMaxHeightCss,
        },
    }),
    ...(isKitchenCarouselCard && {
        ".catalog-img-area": {
            height: kitchenImgAreaHoverHeightCss,
        },
    }),
});

/** Невидимый блок в потоке: резервирует место в сетке под карточку. */
export const catalogCardFlowSizerSx = {
    flex: "0 0 auto",
    width: "100%",
    height: getCatalogGridCardFlowSizerHeightCss(),
    pointerEvents: "none",
    [HOVER_NONE_MQ]: {
        height: getCatalogGridCardFlowSizerHeightCss({withButtonSlot: true}),
    },
};

const catalogCardTitleBaseSx = {
    fontSize: `${CATALOG_CARD_TITLE_FONT_SIZE_PX}px`,
    fontWeight: 600,
    lineHeight: CATALOG_CARD_TITLE_LINE_HEIGHT,
    width: "100%",
    minWidth: 0,
    minHeight: `${CATALOG_CARD_TITLE_BLOCK_MIN_HEIGHT_PX}px`,
    display: "-webkit-box",
    WebkitLineClamp: CATALOG_CARD_TITLE_LINE_COUNT,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
};

const CatalogCardTitle = ({name}) => {
    const titleRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
        const element = titleRef.current;
        if (!element) {
            return undefined;
        }

        const updateOverflow = () => {
            setIsOverflowing(element.scrollHeight > element.clientHeight + 1);
        };

        updateOverflow();

        const resizeObserver = new ResizeObserver(updateOverflow);
        resizeObserver.observe(element);

        return () => resizeObserver.disconnect();
    }, [name]);

    return (
        <Tooltip
            title={name}
            placement="top"
            arrow
            disableHoverListener={!isOverflowing}
            disableFocusListener={!isOverflowing}
            disableTouchListener={!isOverflowing}
        >
            <Typography
                ref={titleRef}
                component="span"
                sx={{
                    ...catalogCardTitleBaseSx,
                    display: "block",
                }}
            >
                {name}
            </Typography>
        </Tooltip>
    );
};

const CARD_TRANSITION_DURATION = "0.45s";
const CARD_TRANSITION_EASING = "cubic-bezier(0.4, 0, 0.2, 1)";
const CARD_IMAGE_TRANSITION = `height ${CARD_TRANSITION_DURATION} ${CARD_TRANSITION_EASING}`;
const CARD_BUTTON_SLOT_TRANSITION = `max-height ${CARD_TRANSITION_DURATION} ${CARD_TRANSITION_EASING}`;
const CARD_KITCHEN_IMG_AREA_TRANSITION = `height ${CARD_TRANSITION_DURATION} ${CARD_TRANSITION_EASING}`;
const FLUID_HALF_COMPACT_HEIGHT = 700;
const FLUID_HALF_COMPACT_HEIGHT_1250 = 580;

export const KITCHEN_CARD_HEIGHT_DEFAULT = 830;
export const KITCHEN_CARD_HEIGHT_1450 = FLUID_HALF_COMPACT_HEIGHT;
export const KITCHEN_CARD_HEIGHT_1250 = FLUID_HALF_COMPACT_HEIGHT_1250;

export const KITCHEN_CARD_LAYOUT = {
    default: {padding: 24, gap: 24, contentHeight: 200},
    at1450: {padding: 16, gap: 16, contentHeight: 190},
    at1250: {padding: 16, gap: 16, contentHeight: 175},
};

/** Fallback text block height (card-content) for kitchen carousel height estimate. */
export const KITCHEN_CAROUSEL_CARD_TEXT_BLOCK_HEIGHT = 280;
export const KITCHEN_CAROUSEL_CARD_TEXT_BLOCK_HEIGHT_COMPACT = 260;

export const KITCHEN_CAROUSEL_CARD_SELECTOR = ".kitchen-carousel-card .catalog-card";

/** card width + gap + text block; square image uses inner width (cardWidth - 2×padding). */
/** Максимальная ширина карточки и карусели кухонь на планшете и уже. */
export const KITCHEN_CAROUSEL_MAX_WIDTH_1023_PX = 800;

export const getKitchenCarouselCardHeightPx = (
    cardWidthPx,
    {padding, gap, contentHeight, imageHeightRatio = 1},
) => Math.round(
    padding * 2
    + Math.max(0, (cardWidthPx - padding * 2) * imageHeightRatio)
    + gap
    + contentHeight,
);

export const getKitchenCarouselCardWrapperSx = (fullWidth = false) => ({
    width: fullWidth ? "100%" : "calc(50% - 10px)",
    minWidth: 0,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignSelf: "flex-start",
    overflow: "visible",
    "& > .catalog-card": {
        width: "100%",
        height: "auto",
        flex: "0 0 auto",
    },
    "@media (max-width: 1023px)": {
        width: "100%",
        maxWidth: `${KITCHEN_CAROUSEL_MAX_WIDTH_1023_PX}px`,
    },
});

export const getKitchenCarouselNavTopPercentFromCardWidth = (
    cardWidthPx,
    layout = KITCHEN_CARD_LAYOUT.default,
) => {
    if (!Number.isFinite(cardWidthPx) || cardWidthPx <= 0) {
        return 50;
    }
    const {padding} = layout;
    const imageHeightRatio = layout?.imageHeightRatio ?? 1;
    const imageHeight = Math.max(0, (cardWidthPx - padding * 2) * imageHeightRatio);
    const cardHeight = getKitchenCarouselCardHeightPx(cardWidthPx, layout);
    return ((padding + imageHeight * KITCHEN_CAROUSEL_NAV_IMAGE_TOP_RATIO) / cardHeight) * 100;
};

/** Top offset within the image (0 = image top, 1 = image bottom), as % of card height. */
export const KITCHEN_CAROUSEL_NAV_IMAGE_TOP_RATIO = 3 / 4;

export function getKitchenCarouselImageCenterTopPercent(
    cardHeight,
    {padding, gap, contentHeight},
    imageTopRatio = KITCHEN_CAROUSEL_NAV_IMAGE_TOP_RATIO,
) {
    const imageHeight = Math.max(0, cardHeight - padding * 2 - gap - contentHeight);
    return ((padding + imageHeight * imageTopRatio) / cardHeight) * 100;
}

const parsePx = (value) => Number.parseFloat(String(value));

const getLayoutImageHeightPx = (cardHeight, {padding, gap, contentHeight}) =>
    Math.max(0, parsePx(cardHeight) - padding * 2 - gap - contentHeight);

const buildImageHeights = (cardHeight, layout) => {
    const defaultPx = getLayoutImageHeightPx(cardHeight, layout);
    return {
        default: `${defaultPx}px`,
        hover: `${Math.max(0, defaultPx - CARD_BUTTON_HEIGHT)}px`,
    };
};

const CardByCarousel = ({
                            item,
                            width = "790px",
                            height = '830px',
                            widthImg = "742px",
                            heightImg = '549px',
                            minHeight,
                            onClick,
                            compactBelow1440 = false,
                            compactMinHeight,
                            compactImageHeight,
                            fluidHalf = false,
                            catalogCard = false,
                            kitchenCarouselCard = false,
                            picturePriorityIndex = 0,
                            draftCard = false,
                            onDelete,
                        }) => {
    const isCatalogGridCard = catalogCard && !kitchenCarouselCard;
    const isKitchenCarouselCard = catalogCard && kitchenCarouselCard;
    const showImageOverlays = !catalogCard || isCatalogGridCard || isKitchenCarouselCard;
    const catalogSizeLines = catalogCard
        ? (draftCard ? formatCatalogDraftCardSizeLines(item) : formatCatalogCardSizeLines(item))
        : [];
    const sortedPictures = getSortedCompositionPictures(item);
    const imageUrl = getCompositionImageUrl(
        item,
        sortedPictures[picturePriorityIndex] ?? sortedPictures[0],
    );
    const resolvedMinHeight = minHeight ?? height;
    const compactResolvedHeight = compactMinHeight ?? resolvedMinHeight;
    const compactImageHeightPx = parsePx(compactImageHeight ?? heightImg);
    const baseImageHeightPx = parsePx(heightImg);
    const hoverImageHeight = `${Math.max(0, baseImageHeightPx - CARD_BUTTON_HEIGHT)}px`;
    const compactHoverImageHeight = `${Math.max(0, compactImageHeightPx - CARD_BUTTON_HEIGHT)}px`;
    const fluidHalfImageHeights = fluidHalf ? {
        default: buildImageHeights(height, KITCHEN_CARD_LAYOUT.default),
        at1450: buildImageHeights(KITCHEN_CARD_HEIGHT_1450, KITCHEN_CARD_LAYOUT.at1450),
        at1250: buildImageHeights(KITCHEN_CARD_HEIGHT_1250, KITCHEN_CARD_LAYOUT.at1250),
    } : null;
    const compactSx = compactBelow1440 ? {
        [CARD_COMPACT_MQ]: {
            ...(!catalogCard && {
                height: compactResolvedHeight,
                minHeight: compactResolvedHeight,
            }),
            padding: "16px 16px 16px 16px",
            gap: "16px",
        },
    } : {};
    const catalogCardSx = catalogCard ? {
        display: "flex",
        flexDirection: "column",
        ...(isCatalogGridCard
            ? {
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                width: "100%",
                height: "auto",
                minHeight: "100%",
                zIndex: 1,
                padding: `${CATALOG_GRID_CARD_PADDING_TOP_PX}px 20px ${CATALOG_GRID_CARD_PADDING_BOTTOM_PX}px`,
                gap: `${CATALOG_GRID_CARD_INNER_GAP_PX}px`,
            }
            : {
                height: "auto",
                minHeight: "unset",
                flex: "0 0 auto",
                ...(isKitchenCarouselCard && {containerType: "inline-size"}),
            }),
    } : {};
    const catalogImageOverlayRootSx = {
        position: "absolute",
        inset: 0,
        zIndex: 1,
        padding: "16px",
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
    };
    const catalogCardContentWrapperSx = catalogCard ? {
        flex: isCatalogGridCard ? "0 0 auto" : "1 1 auto",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
    } : {
        flex: 1,
    };
    const catalogButtonSlotSx = catalogCard ? {
        maxHeight: 0,
        overflow: "hidden",
        flexShrink: 0,
        transition: CARD_BUTTON_SLOT_TRANSITION,
    } : {};
    const catalogButtonSlotInnerSx = catalogCard ? {
        boxSizing: "border-box",
        height: `${CATALOG_CARD_BUTTON_SLOT_MAX_PX}px`,
        paddingTop: `${CATALOG_CARD_BUTTON_GAP_PX}px`,
        display: "flex",
        alignItems: "center",
    } : {};
    const catalogImgSx = catalogCard ? {
        width: "100%",
        maxWidth: "100%",
        flexShrink: 0,
        padding: 0,
        display: "block",
        overflow: "hidden",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundColor: (theme) => theme.palette.background.grey,
        ...(isCatalogGridCard
            ? {
                position: "relative",
                flex: "1 1 auto",
                minHeight: 0,
                height: "100%",
                maxHeight: "100%",
            }
            : {
                position: "relative",
                height: "100%",
            }),
    } : {};
    const compactImgSx = compactBelow1440 ? {
        [CARD_COMPACT_MQ]: {
            ...(!catalogCard && {
                padding: "12px",
                height: compactImageHeight ?? heightImg,
            }),
        },
    } : {};
    const compactImgHoverSx = compactBelow1440 ? {
        [CARD_COMPACT_MQ]: {
            height: compactHoverImageHeight,
        },
    } : {};
    const compactFavoriteSx = compactBelow1440 ? {
        [CARD_COMPACT_MQ]: {
            width: "32px",
            height: "32px",
            "& .MuiSvgIcon-root": {
                fontSize: 20,
            },
        },
    } : {};
    const compactContentSx = compactBelow1440 ? {
        [CARD_COMPACT_MQ]: {
            gap: "12px",
        },
    } : {};
    const compactDetailsSx = compactBelow1440 ? {
        [CARD_COMPACT_MQ]: {
            gap: "8px",
        },
    } : {};
    const compactSpecsSx = compactBelow1440 ? {
        [CARD_COMPACT_MQ]: {
            gap: "6px",
        },
    } : {};
    const compactBadgeSx = compactBelow1440 ? {
        [CARD_COMPACT_MQ]: {
            padding: "4px",
        },
    } : {};
    const compactTagSx = compactBelow1440 ? {
        [CARD_COMPACT_MQ]: {
            padding: "3px",
        },
    } : {};
    const compactLabelsSx = compactBelow1440 ? {
        [CARD_COMPACT_MQ]: {
            gap: "3px",
        },
    } : {};
    const compactKitchenViewSx = compactBelow1440 ? {
        [CARD_COMPACT_MQ]: {
            padding: "2px",
        },
    } : {};
    const compactButtonSx = compactBelow1440 ? {
        [CARD_COMPACT_MQ]: {
            padding: "6px 14px",
        },
    } : {};
    const catalogCardContentSx = catalogCard ? {
        gap: isCatalogGridCard ? "10px" : "12px",
        [CARD_COMPACT_MQ]: {
            gap: isCatalogGridCard ? "8px" : "8px",
        },
    } : {};
    const catalogCardDetailsSx = catalogCard ? {
        gap: isCatalogGridCard ? "4px" : "6px",
        ...(isCatalogGridCard && {
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
        }),
        [CARD_COMPACT_MQ]: {
            gap: "4px",
        },
    } : {};
    const catalogCardSizeSectionSx = catalogCard ? {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        width: "max-content",
        minWidth: 0,
        maxWidth: "100%",
        [CARD_COMPACT_MQ]: {
            gap: "3px",
        },
    } : {};
    const catalogCardSizeTitleSx = catalogCard ? {
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: 1.2,
    } : {};
    const catalogCardSizeBlockSx = catalogCard ? {
        padding: "2px 4px",
        gap: catalogSizeLines.length > 1 ? "1px" : 0,
        [CARD_COMPACT_MQ]: {
            padding: "2px 4px",
        },
    } : {};
    const catalogCardDescriptionSx = catalogCard ? {
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
    } : {};
    const catalogCardPriceRowSx = isCatalogGridCard ? {
        flexShrink: 0,
        marginTop: "auto",
    } : {};
    const fluidHalfSx = fluidHalf ? {
        width: "calc(50% - 10px)",
        minWidth: 0,
        flexShrink: 0,
        height,
        minHeight: resolvedMinHeight,
        overflow: "hidden",
        [FLUID_HALF_COMPACT_MQ]: {
            height: FLUID_HALF_COMPACT_HEIGHT,
            minHeight: FLUID_HALF_COMPACT_HEIGHT,
            padding: "16px",
            gap: "16px",
        },
        [FLUID_HALF_COMPACT_MQ_1250]: {
            height: FLUID_HALF_COMPACT_HEIGHT_1250,
            minHeight: FLUID_HALF_COMPACT_HEIGHT_1250,
        },
    } : {};
    const fluidHalfImgSx = fluidHalf ? {
        width: "100%",
        flex: "0 0 auto",
        aspectRatio: "unset",
        height: fluidHalfImageHeights.default.default,
        [FLUID_HALF_COMPACT_MQ]: {
            padding: "12px",
            height: fluidHalfImageHeights.at1450.default,
        },
        [FLUID_HALF_COMPACT_MQ_1250]: {
            height: fluidHalfImageHeights.at1250.default,
        },
    } : {};
    const fluidHalfImgHoverSx = fluidHalf ? {
        height: fluidHalfImageHeights.default.hover,
        [FLUID_HALF_COMPACT_MQ]: {
            height: fluidHalfImageHeights.at1450.hover,
        },
        [FLUID_HALF_COMPACT_MQ_1250]: {
            height: fluidHalfImageHeights.at1250.hover,
        },
    } : {
        height: hoverImageHeight,
        ...compactImgHoverSx,
    };
    const imgHoverSx = fluidHalfImgHoverSx;
    const CatalogImgContainer = catalogCard ? Box : React.Fragment;
    const catalogGridImgAreaRestMaxHeightCss = `calc(100cqw * ${CATALOG_GRID_IMAGE_HEIGHT_CQW_RATIO})`;
    const catalogGridImgAreaHoverMaxHeightCss = `max(0px, calc(100cqw * ${CATALOG_GRID_IMAGE_HEIGHT_CQW_RATIO} - ${CATALOG_CARD_BUTTON_SLOT_MAX_PX}px))`;
    const kitchenImgAreaRestHeightCss = `calc(100cqw * ${KITCHEN_CAROUSEL_IMAGE_HEIGHT_CQW_RATIO})`;
    const kitchenImgAreaHoverHeightCss =
        `max(0px, calc(100cqw * ${KITCHEN_CAROUSEL_IMAGE_HEIGHT_CQW_RATIO} - ${CATALOG_CARD_BUTTON_SLOT_MAX_PX}px))`;
    const catalogImgContainerProps = catalogCard ? {
        className: "catalog-img-area",
        sx: {
            width: "100%",
            flexShrink: 0,
            containerType: "inline-size",
            ...(isCatalogGridCard && {
                flex: "1 1 auto",
                minHeight: 0,
                maxHeight: catalogGridImgAreaRestMaxHeightCss,
                display: "flex",
                flexDirection: "column",
                transition: `max-height ${CARD_TRANSITION_DURATION} ${CARD_TRANSITION_EASING}`,
            }),
            ...(isKitchenCarouselCard && {
                height: kitchenImgAreaRestHeightCss,
                transition: CARD_KITCHEN_IMG_AREA_TRANSITION,
            }),
        },
    } : {};
    const fluidHalfContentSx = fluidHalf ? {
        [FLUID_HALF_COMPACT_MQ]: {
            gap: "12px",
        },
    } : {};
    const fluidHalfDetailsSx = fluidHalf ? {
        [FLUID_HALF_COMPACT_MQ]: {
            gap: "8px",
        },
    } : {};
    const fluidHalfSpecsSx = fluidHalf ? {
        [FLUID_HALF_COMPACT_MQ]: {
            gap: "6px",
        },
    } : {};
    const cardImageOverlayItemSx = {
        flexShrink: 0,
        textAlign: "left",
    };
    const imageOverlayActionSx = {
        display: 'flex',
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        bgcolor: (theme) => theme.palette.primary.light50,
        ...compactFavoriteSx,
        ...cardImageOverlayItemSx,
    };
    const renderPromoBadges = () => {
        if (draftCard) {
            return null;
        }
        return (
            <>
                {item.discount && <Box sx={{
                    width: "max-content",
                    boxSizing: "border-box",
                    padding: "8px",
                    ...compactBadgeSx,
                    borderRadius: "4px",
                    bgcolor: (theme) => theme.palette.error.light,
                    color: "white",
                    ...cardImageOverlayItemSx,
                }}>
                    <Typography sx={{
                        fontSize: "18px", fontWeight: 600, lineHeight: 1.2,
                        ...cardImageOverlayItemSx,
                    }}>-{item.discountAmount}%</Typography>
                </Box>}
                {item.top && <Box sx={{
                    width: "max-content",
                    boxSizing: "border-box",
                    padding: "8px",
                    ...compactBadgeSx,
                    borderRadius: "4px",
                    bgcolor: (theme) => theme.palette.success.light,
                    color: "white",
                    ...cardImageOverlayItemSx,
                }}>
                    <Typography sx={{
                        fontSize: "18px", fontWeight: 600, lineHeight: 1.2,
                        ...cardImageOverlayItemSx,
                    }}>Хит продаж!</Typography>
                </Box>}
            </>
        );
    };
    const renderImageOverlayAction = () => {
        if (draftCard && onDelete) {
            return (
                <Box
                    component="button"
                    type="button"
                    aria-label="Удалить черновик"
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onDelete(item);
                    }}
                    sx={{
                        ...imageOverlayActionSx,
                        borderRadius: "8px",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                    }}
                >
                    <CloseIcon sx={{fontSize: 24}}/>
                </Box>
            );
        }
        if (!draftCard) {
            return (
                <Box sx={imageOverlayActionSx}>
                    <FavoriteBorderOutlinedIcon sx={{fontSize: 24}}/>
                </Box>
            );
        }
        return null;
    };

    return <Box
        className={catalogCard ? "catalog-card" : undefined}
        sx={{
        ...(catalogCard
            ? {height: "auto", minHeight: "unset"}
            : {height, minHeight: resolvedMinHeight}),
        width: width,
        display: 'flex',
        flexDirection: "column",
        boxSizing: "border-box",
        backgroundColor: (theme) => theme.palette.background.grey,
        borderRadius: "16px",
        padding: "24px 24px 24px 24px",
        gap: "24px",
        ...catalogCardSx,
        ...compactSx,
        ...fluidHalfSx,
        overflow: (isCatalogGridCard || isKitchenCarouselCard) ? "visible" : "hidden",
        border: catalogCard ? "1px solid #EAEBEA" : "1px solid transparent",
        ...(catalogCard
            ? {
                [HOVER_HOVER_MQ]: {
                    "&:hover": {
                        zIndex: (isCatalogGridCard || isKitchenCarouselCard) ? 2 : undefined,
                        border: (theme) => `1px solid ${theme.palette.primary.main}`,
                        ...buildCatalogCardExpandedInnerSx({
                            isCatalogGridCard,
                            isKitchenCarouselCard,
                            catalogGridImgAreaHoverMaxHeightCss,
                            kitchenImgAreaHoverHeightCss,
                        }),
                    },
                },
                [HOVER_NONE_MQ]: buildCatalogCardExpandedInnerSx({
                    isCatalogGridCard,
                    isKitchenCarouselCard,
                    catalogGridImgAreaHoverMaxHeightCss,
                    kitchenImgAreaHoverHeightCss,
                }),
            }
            : {
                [HOVER_HOVER_MQ]: {
                    "&:hover": {
                        ".img-box": imgHoverSx,
                    },
                },
            }),
    }}>
        <CatalogImgContainer {...catalogImgContainerProps}>
        <Box className="img-box"
             sx={{
                 boxSizing: "border-box",
                 ...(!catalogCard && {height: heightImg}),
                 width: widthImg,
                 borderRadius: "16px",
                 backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
                 ...(!catalogCard && {
                     backgroundRepeat: "no-repeat",
                     backgroundSize: "cover",
                 }),
                 padding: catalogCard ? 0 : "16px",
                 ...catalogImgSx,
                 ...compactImgSx,
                 ...fluidHalfImgSx,
                 display: catalogCard ? "block" : "flex",
                 justifyContent: catalogCard ? undefined : "space-between",
                 ...(!catalogCard && {
                     transition: `height 1.5s ${CARD_TRANSITION_EASING}`,
                 }),
             }}>
            {showImageOverlays && (catalogCard ? (
                <Box sx={catalogImageOverlayRootSx}>
                    <Box sx={{
                        boxSizing: "border-box",
                        display: "flex",
                        justifyContent: "space-between",
                        flexDirection: "column",
                        alignSelf: "stretch",
                        minHeight: "100%",
                        flex: "1 1 auto",
                        ...cardImageOverlayItemSx,
                    }}>
                        <Box sx={{
                            boxSizing: "border-box",
                            width: "146px",
                            padding: "4px",
                            borderRadius: "4px",
                            border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
                            bgcolor: (theme) => theme.palette.primary.lightGrey,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            ...compactKitchenViewSx,
                            ...cardImageOverlayItemSx,
                            textAlign: "center",
                        }}>
                            <Typography sx={{
                                fontSize: "14px",
                                fontWeight: 400,
                                lineHeight: 1.2,
                                textAlign: "center",
                            }}>Вид кухни: <span
                                style={{fontWeight: 600}}> {item.cornerType === "STRAIGHT" ? "Прямая" : "Угловая"}</span>
                            </Typography>
                        </Box>
                        {!draftCard && <Box sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                            ...compactLabelsSx,
                            ...cardImageOverlayItemSx,
                        }}>
                            {renderPromoBadges()}
                        </Box>}
                    </Box>
                    {renderImageOverlayAction()}
                </Box>
            ) : (
                <>
                    <Box sx={{
                        boxSizing: "border-box",
                        display: "flex",
                        justifyContent: "space-between",
                        flexDirection: "column",
                        height: "100%",
                        ...cardImageOverlayItemSx,
                    }}>
                        <Box sx={{
                            boxSizing: "border-box",
                            width: "146px",
                            padding: "4px",
                            borderRadius: "4px",
                            border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
                            bgcolor: (theme) => theme.palette.primary.lightGrey,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            ...compactKitchenViewSx,
                            ...cardImageOverlayItemSx,
                            textAlign: "center",
                        }}>
                            <Typography sx={{
                                fontSize: "14px",
                                fontWeight: 400,
                                lineHeight: 1.2,
                                textAlign: "center",
                            }}>Вид кухни: <span
                                style={{fontWeight: 600}}> {item.cornerType === "STRAIGHT" ? "Прямая" : "Угловая"}</span>
                            </Typography>
                        </Box>
                        {!draftCard && <Box sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                            ...compactLabelsSx,
                            ...cardImageOverlayItemSx,
                        }}>
                            {renderPromoBadges()}
                        </Box>}
                    </Box>
                    {renderImageOverlayAction()}
                </>
            ))}
        </Box>
        </CatalogImgContainer>
        <Box className="card-content" sx={{
            ...catalogCardContentWrapperSx,
            minHeight: 0,
            ...(!catalogCard && {overflow: "hidden"}),
            gap: "20px",
            ...compactContentSx,
            ...fluidHalfContentSx,
            ...catalogCardContentSx,
            display: 'flex',
            flexDirection: "column",
        }}>
            {catalogCard ? (
                <CatalogCardTitle name={item.name}/>
            ) : (
                <Typography sx={{
                    fontSize: "22px", fontWeight: 600, lineHeight: 1.2,
                }}>{item.name}</Typography>
            )}
            <Box
                className={catalogCard ? "catalog-card-details" : undefined}
                sx={{
                gap: "12px",
                ...compactDetailsSx,
                ...fluidHalfDetailsSx,
                ...catalogCardDetailsSx,
                display: 'flex',
                flexDirection: "column",
            }}>
                {catalogCard ? (
                    <Box sx={catalogCardSizeSectionSx}>
                        <Typography sx={catalogCardSizeTitleSx}>
                            {draftCard ? "Размеры" : "Размерный ряд"}:
                        </Typography>
                        <Box sx={{
                            boxSizing: "border-box",
                            width: "100%",
                            maxWidth: "100%",
                            height: catalogSizeLines.length > 1 ? "auto" : "26px",
                            minHeight: catalogSizeLines.length > 1 ? "26px" : undefined,
                            padding: "4px",
                            display: "flex",
                            flexDirection: "column",
                            gap: catalogSizeLines.length > 1 ? "2px" : 0,
                            ...compactTagSx,
                            ...catalogCardSizeBlockSx,
                            borderRadius: "4px",
                            border: (theme) => `1px solid ${theme.palette.primary.main}`,
                            bgcolor: (theme) => theme.palette.primary.light,
                        }}>
                            {catalogSizeLines.map(({label, range}) => (
                                <Typography
                                    key={label}
                                    component="span"
                                    sx={{
                                        minWidth: 0,
                                        maxWidth: "100%",
                                        fontSize: "14px",
                                        fontWeight: 400,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {label}: <span style={{fontWeight: 600, overflowWrap: "anywhere", wordBreak: "break-word"}}>{range}</span>
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{
                        boxSizing: "border-box",
                        width: "max-content",
                        height: "26px",
                        padding: "4px",
                        display: "flex",
                        flexDirection: "column",
                        ...compactTagSx,
                        borderRadius: "4px",
                        border: (theme) => `1px solid ${theme.palette.primary.main}`,
                        bgcolor: (theme) => theme.palette.primary.light,
                    }}>
                        <Typography sx={{
                            fontSize: "14px", fontWeight: 400, lineHeight: 1.2,
                        }}>Размер, мм: <span
                            style={{fontWeight: 600}}> {formatCompositionSizeMm(item)}</span>
                        </Typography>
                    </Box>
                )}
                <Typography sx={{
                    fontSize: "16px", fontWeight: 400, lineHeight: 1.2,
                    ...catalogCardDescriptionSx,
                }}>{item.description}</Typography>
                <Box sx={{
                    gap: "8px",
                    ...compactSpecsSx,
                    ...fluidHalfSpecsSx,
                    display: 'flex',
                    flexDirection: "column",
                }}>
                    <Typography sx={{
                        fontSize: "14px", fontWeight: 400, lineHeight: 1.2,
                    }}>Корпус: <span style={{fontWeight: 600}}> {formatCompositionShortTopBottom(item.nameShortBodyTop, item.nameShortBodyBottom)}</span></Typography>
                    <Typography sx={{
                        fontSize: "14px", fontWeight: 400, lineHeight: 1.2,
                    }}>Фасад: <span style={{fontWeight: 600}}>{formatCompositionShortTopBottom(item.nameShortFrontTop, item.nameShortFrontBottom)}</span></Typography>
                    <Typography sx={{
                        fontSize: "14px", fontWeight: 400, lineHeight: 1.2,
                    }}>Столешница: <span style={{fontWeight: 600}}>{item.nameShortTableTop ?? ""}</span></Typography>
                </Box>
                <Box sx={{
                    boxSizing: "border-box",
                    width: "max-content",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "4px",
                    ...compactTagSx,
                    ...catalogCardPriceRowSx,
                    borderRadius: "4px",
                    border: (theme) => `1px solid ${theme.palette.primary.main}`,
                }}>
                    <Typography sx={{
                        fontSize: "17px",
                        fontWeight: 600,
                        lineHeight: 1.2,
                        color: item.discount ? (theme) => theme.palette.secondary.add : (theme) => theme.palette.text.main,
                        ...(draftCard && item.actualPrice === false ? {textDecoration: "line-through"} : {}),
                    }}>{formatCatalogPriceRub(item.price, {from: !draftCard})}</Typography>
                    {item.discount && <Typography sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: 1.2,
                        textDecoration: "line-through",
                    }}>{formatCatalogPriceRub(item.price / (1 - item.discountAmount / 100), {from: !draftCard})} </Typography>}
                </Box>
                {catalogCard ? (
                    <Box className="catalog-button-slot" sx={catalogButtonSlotSx}>
                        <Box sx={catalogButtonSlotInnerSx}>
                            <ButtonAdd
                                text={draftCard ? "Редактировать" : "Выбрать шаблон"}
                                width={170}
                                minWidth={184}
                                height={42}
                                onClick={onClick}
                                sx={compactButtonSx}
                            />
                        </Box>
                    </Box>
                ) : null}
            </Box>
            {!catalogCard ? (
                <Box className="button-box" sx={{
                    height: CARD_BUTTON_HEIGHT,
                    flexShrink: 0,
                }}>
                    <ButtonAdd
                        text={draftCard ? "Редактировать" : "Выбрать шаблон"}
                        width={170}
                        minWidth={184}
                        height={42}
                        onClick={onClick}
                        sx={compactButtonSx}
                    />
                </Box>
            ) : null}
        </Box>
    </Box>
}
export default CardByCarousel